// RITA-1525 (Epic RITA-901) — Dokumentformat v2 des Vertragseditors.
//
// Das Vertragsdokument ist die maßgebliche Quelle für Seitenansicht und PDF-Probe. Es beschreibt
// Kopfzeile, Text und Fußzeile als Blöcke aus Inline-Knoten; automatisch berechnete Seiten sind
// nur eine abgeleitete Darstellung (layout/seitenAufteilen.ts) und werden nicht gespeichert.
// Bekannte Platzhalter sind eigene, unteilbare Knoten — der Schutz aus RITA-1475 ergibt sich damit
// aus der Struktur und nicht mehr aus DOM-Markern.
import type { VertragPlatzhalterKey } from './platzhalterkatalog';

export const VERTRAGSDOKUMENT_FORMAT = 'prm-vertragsdokument';
// v2 (28.09.2026): Die Kopfzeile verweist auf eine Vertragsart (Logo aus RITA-1526) statt auf eines
// von vier festen Markenkürzeln. v1 wurde nie gespeichert (Speichern folgt in Teilpaket 4), daher
// gibt es keine Umstellung alter Dokumente.
export const VERTRAGSDOKUMENT_VERSION = 2;

export type HorizontalePosition = 'links' | 'mitte' | 'rechts';

/**
 * Zeichenformat von Textläufen und Platzhaltern: Gespeichert wird nur `true`, ein fehlendes Merkmal heißt „aus“.
 * `groessePt` (RITA-1534) ist die eigene Schriftgröße in Punkt; fehlt sie, gilt die Grundgröße des Bereichs
 * (`schrift.groessePt`, in der Fußzeile die feste Standardgröße aus `model/schriftgroessen.ts`).
 */
export interface Zeichenformat {
  fett?: boolean;
  kursiv?: boolean;
  unterstrichen?: boolean;
  groessePt?: number;
}

export interface Textlauf extends Zeichenformat {
  typ: 'text';
  text: string;
}

/**
 * Ein bekannter Platzhalter aus dem Katalog des Vertragseditors (gemeinsamer Katalog plus eigene Platzhalter der
 * Anschreiben, RITA-1535); beim Erzeugen wird er mit Beraterdaten gefüllt.
 * Sein Format gilt für den eingesetzten Wert (RITA-1533); ein Platzhalter wird immer ganz formatiert.
 */
export interface Platzhalterknoten extends Zeichenformat {
  typ: 'platzhalter';
  schluessel: VertragPlatzhalterKey;
}

/** Ein Ankreuzkästchen, das der Kunde leer oder angekreuzt in die Vorlage setzt. */
export interface Kaestchenknoten {
  typ: 'kaestchen';
  angekreuzt: boolean;
}

export interface Zeilenumbruchknoten {
  typ: 'zeilenumbruch';
}

export type Inlineknoten = Textlauf | Platzhalterknoten | Kaestchenknoten | Zeilenumbruchknoten;

export interface Absatz {
  typ: 'absatz';
  id: string;
  inhalt: Inlineknoten[];
}

export interface Liste {
  typ: 'liste';
  id: string;
  eintraege: Inlineknoten[][];
}

/** Ein vom Kunden gesetzter Seitenumbruch; der folgende Block beginnt auf einer neuen Seite. */
export interface Seitenumbruch {
  typ: 'seitenumbruch';
  id: string;
}

export type Block = Absatz | Liste | Seitenumbruch;

/** Blöcke, die in Kopf- und Fußzeile erlaubt sind (kein Seitenumbruch). */
export type Zeilenblock = Absatz | Liste;

export interface Seitenraender {
  obenMm: number;
  rechtsMm: number;
  untenMm: number;
  linksMm: number;
}

export interface Seiteneinstellungen {
  format: 'A4';
  raender: Seitenraender;
  kopfHoeheMm: number;
  fussHoeheMm: number;
}

/**
 * Schrift und Abstände des ganzen Dokuments einschließlich Kopf- und Fußzeile. Zeilenabstand und Abstand nach
 * Absatz sind seit RITA-1571 in der Werkzeugleiste wählbar (Werte und Bezeichnungen in `model/abstaende.ts`).
 */
export interface Schrifteinstellungen {
  /** Fest mitgelieferte Schrift (assets/fonts), damit Ansicht und PDF gleich umbrechen. */
  familie: 'Liberation Sans';
  groessePt: number;
  /** Einheitenloser Zeilenhöhenfaktor wie CSS `line-height` (1.15 = Word „einfach“; Vorlagen vor RITA-1571: 1.4). */
  zeilenhoehe: number;
  /**
   * Abstand nach jedem Absatz und jeder Aufzählung in em der Grundgröße des Bereichs (RITA-1571). Fehlt er, gilt der
   * bisherige Abstand von 0.6 em: Vorlagen von vor RITA-1571 bleiben so unverändert, bis jemand ihn wählt.
   */
  absatzabstandEm?: number;
}

export interface Logoeinstellung {
  /** id der Vertragsart, deren Logo die Kopfzeile zeigt; null = kein Logo. Das Bild liegt an der Vertragsart. */
  vertragsartId: number | null;
  position: HorizontalePosition;
  hoeheMm: number;
}

export interface Kopfzeile {
  logo: Logoeinstellung;
  inhalt: Zeilenblock[];
}

export interface Seitenzahleinstellung {
  anzeigen: boolean;
  position: HorizontalePosition;
}

export interface Fusszeile {
  inhalt: Zeilenblock[];
  seitenzahl: Seitenzahleinstellung;
}

export interface Vertragsdokument {
  format: typeof VERTRAGSDOKUMENT_FORMAT;
  version: typeof VERTRAGSDOKUMENT_VERSION;
  seite: Seiteneinstellungen;
  schrift: Schrifteinstellungen;
  kopf: Kopfzeile;
  text: Block[];
  fuss: Fusszeile;
}
